import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { ImageFileData, SocratesImage, } from '@socrates/common';
import { IImagesRepo } from '../interfaces/iimages.repo';
import { UrlPath, } from '../../services/UrlPath';

@Injectable()
export class ImagesRepo extends IImagesRepo {
	// In-flight promise cache — prevents duplicate concurrent requests for the same path
	private _inFlight: Map<string, Promise<SocratesImage>> = new Map();
	private _imageCache: Map<string, SocratesImage> = new Map();

	constructor(
		private httpClient: HttpClient,
		private urlPath: UrlPath
	) {
		super();
	}

	async postImage(urlPathSuffix: string, image: SocratesImage): Promise<SocratesImage> {
		if (this._inFlight.has(urlPathSuffix)) {
			return this._inFlight.get(urlPathSuffix);
		}
		const imageCopy: any = instanceToPlain(image.imageData, { excludePrefixes: ['_'] });
		imageCopy.data = image.imageData.rawData;
		const restPath = this.urlPath.getImageServicePath(urlPathSuffix);

		const promise = firstValueFrom(this.httpClient.post(restPath, imageCopy))
			.then((response: any) => {
				if (!response?.success) throw response;
				image.url = restPath;
				return image;
			})
			.finally(() => this._inFlight.delete(urlPathSuffix));

		this._inFlight.set(urlPathSuffix, promise);
		return promise;
	}

	async pullImage(urlPathSuffix: string, force = false): Promise<SocratesImage> {
		const cached = this._imageCache.get(urlPathSuffix);
		if (!force && cached) return cached;

		if (!force && this._inFlight.has(urlPathSuffix)) {
			return this._inFlight.get(urlPathSuffix);
		}

		const restPath = this.urlPath.getImageServicePath(urlPathSuffix);
		const promise = firstValueFrom(
			this.httpClient.get(restPath, { responseType: 'blob', observe: 'response' })
		).then(async (response: HttpResponse<any>) => {
			if (response.status < 210 && response.body) {
				const data = await response.body.text();
				const image = new SocratesImage();
				image.imageData.contentType = response.headers.get('content-type');
				image.imageData.size = +response.headers.get('content-length');
				image.imageData.name = response.headers.get('content-filename');
				image.imageData.rawData = data;
				image.url = restPath;
				this._imageCache.set(urlPathSuffix, image);
				return image;
			} else if (response.status < 210) {
				return new SocratesImage();
			}
			throw response;
		}).finally(() => this._inFlight.delete(urlPathSuffix));

		this._inFlight.set(urlPathSuffix, promise);
		return promise;
	}

	async pullRootImage(urlPathSuffix: string): Promise<SocratesImage> {
		if (this._inFlight.has(urlPathSuffix)) {
			return this._inFlight.get(urlPathSuffix);
		}
		const restPath = this.urlPath.getRootPath(urlPathSuffix);
		const promise = firstValueFrom(
			this.httpClient.get(restPath, { responseType: 'blob', observe: 'response' })
		).then(async (response: HttpResponse<any>) => {
			if (response.status === 200) {
				const data = await response.body.text();
				const image = new SocratesImage();
				image.imageData.contentType = response.headers.get('content-type');
				image.imageData.size = +response.headers.get('content-length');
				image.imageData.name = response.headers.get('content-filename');
				image.imageData.rawData = data;
				return image;
			}
			throw response;
		}).finally(() => this._inFlight.delete(urlPathSuffix));

		this._inFlight.set(urlPathSuffix, promise);
		return promise;
	}

	pullAddressImage(urlPathSuffix: string, _position: any): Promise<SocratesImage> {
		return this.pullImage(urlPathSuffix);
	}

	async removeImage(imageUrl: string): Promise<boolean> {
		if (!imageUrl) throw new Error('no image path');
		const imagePath = this.urlPath.getImageServicePath(`designs/${ imageUrl }`);
		const response: any = await firstValueFrom(this.httpClient.delete(imagePath));
		if (!response?.success) throw new Error('Error removing image');
		return true;
	}

	async tunnelImage(imageUrl: string): Promise<SocratesImage> {
		if (!imageUrl) return null;
		const imagePath = this.urlPath.getImageServicePath('tunnelImage?url=":imageUrl"', { imageUrl });
		const response: any = await firstValueFrom(
			this.httpClient.get(imagePath, { observe: 'response' })
		);
		if (response.status !== 200) throw new Error('Error retrieving image');
		if (!response.headers.get('tunnel-succeeded')) return null;
		const image = new SocratesImage();
		image.url = imageUrl;
		image.imageData = plainToInstance(ImageFileData, response.body);
		return image;
	}
}
